'use client';

import SearchField from '@/components/SearchForm/SearchField';
import SelectedFilters from '@/components/SearchForm/SelectedFilters';
import useSearchForm, { UseSearchFormProps } from '@/components/SearchForm/useSearchForm';
import { cn } from '@/helpers/common';
import React from 'react';
import IconsSprites from '../IconsSprites';

const fieldClassNames = {
  input: cn(
    'py-2.5 pl-9! px-3 lg:w-full bg-white max-md:text-base md:text-sm default-transition placeholder-kg-jumbo',
    'border border-transparent rounded-xs lg:rounded-xs outline-hidden focus:border-kg-fun-blue',
  ),
  suggestions:
    'top-[calc(100%+4px)] border border-kg-athens-gray rounded-xs bg-white shadow-[0_8px_16px_rgba(5,10,23,0.08)]',
  suggestionElement: 'bg-white hover:bg-kg-athens-gray-light py-2 text-sm',
  suggestionElementActive: 'bg-kg-athens-gray-light',
};

const SearchFormKG: React.FC<UseSearchFormProps & { className?: string }> = ({
  className,
  fromHomePage,
  q,
  l,
  baseUrl,
  queryParameters,
  scrollElementId,
  locations,
  occupations,
  industries,
  seniorities,
}) => {
  const {
    keyword,
    location,
    locationSuggestions,
    keywordSuggestions,
    isKeywordsSuggestionsOpened,
    isLocationsSuggestionsOpened,
    setIsKeywordsSuggestionsOpened,
    setIsLocationsSuggestionsOpened,
    onSubmit,
    onFormKeyDown,
    onKeywordChange,
    onLocationChange,
    cancelKeywordSearch,
    cancelLocationSearch,
    onKeywordSuggestionsClick,
    onLocationSuggestionsClick,
    multipleLocations,
    getRemoveFilterHref,
  } = useSearchForm({
    fromHomePage,
    q,
    l,
    baseUrl,
    queryParameters,
    scrollElementId,
    locations,
    occupations,
    industries,
    seniorities,
  });

  return (
    <div
      className={cn(
        'px-2.5 py-2 rounded-xs bg-white border border-kg-athens-gray',
        className,
      )}
    >
      <IconsSprites />
      <form
        className={'flex flex-col gap-3 md:flex-row md:justify-center md:items-center'}
        onSubmit={onSubmit}
        onKeyDown={onFormKeyDown}
      >
        <SearchField
          key="q-input"
          ariaLabel="Search jobs"
          icon="#lens"
          name="q"
          id="q"
          placeholder={'Titles, skills, company'}
          maxLength={200}
          minLength={1}
          value={keyword}
          onChange={onKeywordChange}
          cancelSearch={cancelKeywordSearch}
          suggestions={keywordSuggestions}
          onSuggestionsClick={onKeywordSuggestionsClick}
          isSuggestionsOpened={isKeywordsSuggestionsOpened}
          setIsSuggestionsOpened={setIsKeywordsSuggestionsOpened}
          className="w-full flex-col md:flex-1"
          classNames={fieldClassNames}
          autoFocus={!keyword}
        />
        <SearchField
          key="l-input"
          ariaLabel="Location"
          icon="#location"
          name="l"
          id="l"
          placeholder={'Location'}
          maxLength={100}
          minLength={1}
          value={location}
          onChange={onLocationChange}
          cancelSearch={cancelLocationSearch}
          suggestions={locationSuggestions}
          onSuggestionsClick={onLocationSuggestionsClick}
          isSuggestionsOpened={isLocationsSuggestionsOpened}
          setIsSuggestionsOpened={setIsLocationsSuggestionsOpened}
          className="w-full flex-col md:flex-1"
          classNames={fieldClassNames}
        />
        <button
          className={cn(
            'h-10 default-disabled default-transition tracking-wider shrink-0',
            'text-white rounded-xs px-6 py-2 bg-kg-fun-blue font-bold text-center hover:bg-kg-deep-sapphire',
          )}
          type="submit"
        >
          SEARCH
        </button>
      </form>
      <SelectedFilters
        locations={multipleLocations}
        occupations={occupations}
        industries={industries}
        seniorities={seniorities}
        getRemoveHref={getRemoveFilterHref}
        classNames={{
          wrapper: 'mt-1 pt-3 pb-2 border-t border-dashed border-kg-athens-gray',
          label: 'text-kg-mako tracking-wider',
          tag: 'text-kg-black-pearl bg-kg-athens-gray-light border border-kg-athens-gray rounded-xs',
          removeIcon: 'text-kg-jumbo hover:text-kg-fun-blue',
        }}
      />
    </div>
  );
};

export default SearchFormKG;
